import { useNavigate, useSearch } from "@tanstack/react-router";
import { Bell01, CheckVerified02 } from "@untitledui/icons";
import { useAdminNavBadges } from "@/api/admin/nav-badges";
import { useAdminProductUpdates } from "@/api/admin/product-updates";
import { Tabs } from "@/components/application/tabs/tabs";
import { CreateNavBadgeModal } from "@/pages/admin/modals/nav-badge-modal";
import { CreateProductUpdateModal } from "@/pages/admin/modals/product-update-modal";
import { PageHeader } from "@/pages/admin/page-header";
import { AdminNavBadgesTable } from "@/pages/admin/tables/nav-badges";
import { AdminProductUpdatesTable } from "@/pages/admin/tables/product-updates";
import type { adminInAppUpdatesRoute } from "@/router";

const productUpdatesTab = { id: "product-updates", label: "Product updates", icon: <CheckVerified02 /> };
const newPageNotificationTab = { id: "new-page-notification", label: "New Page Notification", icon: <Bell01 /> };

const tabs = [productUpdatesTab, newPageNotificationTab];

const countPagination = { offset: 0, limit: 20 };

export const InAppUpdatesPage = () => {
  const { tab } = useSearch({ strict: false }) as ReturnType<(typeof adminInAppUpdatesRoute)["useSearch"]>;
  const navigate = useNavigate() as ReturnType<(typeof adminInAppUpdatesRoute)["useNavigate"]>;

  const selectedTab = tab ?? "product-updates";
  const isNotificationsTab = selectedTab === "new-page-notification";

  const selectTab = (next: string) =>
    navigate({ search: { tab: next as "product-updates" | "new-page-notification" } });

  const { data: productUpdatesData } = useAdminProductUpdates(countPagination, {
    column: "createdAt",
    direction: "desc",
  });
  const { data: navBadgesData } = useAdminNavBadges(countPagination, { column: "createdAt", direction: "desc" });

  return (
    <div className="h-full flex flex-col gap-6 mt-4 px-12 pb-8">
      <PageHeader
        title="In App Updates"
        subtitle="Announcements and new page notifications shown across the app"
        count={isNotificationsTab ? navBadgesData?.total : productUpdatesData?.total}
        actions={isNotificationsTab ? <CreateNavBadgeModal /> : <CreateProductUpdateModal />}
        icon={CheckVerified02}
      />
      <Tabs selectedKey={selectedTab} onSelectionChange={(key) => selectTab(key as string)}>
        <Tabs.List type="underline" items={tabs}>
          {(item) => <Tabs.Item {...item} />}
        </Tabs.List>
      </Tabs>
      <div className="flex-1 min-h-0 flex flex-col overflow-auto">
        {isNotificationsTab ? <AdminNavBadgesTable /> : <AdminProductUpdatesTable />}
      </div>
    </div>
  );
};
